// ===========================================
// ActionMenu — overflow "More" menu for crowded action bars
// ===========================================
// Collapses secondary actions behind a single ⋮ button. Each item keeps its
// icon AND label (better discoverability/accessibility than icon-only buttons),
// and the menu scales as more actions are added. Supports a destructive item
// (rendered red, below a divider) and per-item disabled state.

import { useState } from 'react';
import {
  IconButton,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export interface ActionMenuItem {
  /** Stable key. */
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Tooltip shown on the item (e.g. why it's disabled). */
  title?: string;
  /** Render in red + place below a divider (e.g. Cancel). */
  destructive?: boolean;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Button label; when omitted, renders an icon-only ⋮ button. */
  label?: string;
  /** aria-label for the trigger. */
  ariaLabel?: string;
  buttonSx?: object;
}

export default function ActionMenu({
  items,
  label = 'More',
  ariaLabel = 'More actions',
  buttonSx,
}: ActionMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  if (items.length === 0) return null;

  const normal = items.filter((i) => !i.destructive);
  const destructive = items.filter((i) => i.destructive);

  const handleSelect = (item: ActionMenuItem) => {
    setAnchorEl(null);
    if (!item.disabled) item.onClick();
  };

  const renderItem = (item: ActionMenuItem) => {
    const menuItem = (
      <MenuItem
        key={item.key}
        onClick={() => handleSelect(item)}
        disabled={item.disabled}
        sx={item.destructive ? { color: 'error.main' } : undefined}
      >
        {item.icon && (
          <ListItemIcon sx={item.destructive ? { color: 'error.main' } : undefined}>
            {item.icon}
          </ListItemIcon>
        )}
        <ListItemText>{item.label}</ListItemText>
      </MenuItem>
    );
    // A disabled MenuItem can't show a tooltip on hover, so wrap in a span.
    return item.title ? (
      <Tooltip key={item.key} title={item.title} placement="left">
        <span>{menuItem}</span>
      </Tooltip>
    ) : (
      menuItem
    );
  };

  return (
    <>
      {label ? (
        <Button
          variant="outlined"
          endIcon={<MoreVertIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={ariaLabel}
          sx={buttonSx}
        >
          {label}
        </Button>
      ) : (
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={ariaLabel}
          sx={buttonSx}
        >
          <MoreVertIcon />
        </IconButton>
      )}
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        {normal.map(renderItem)}
        {destructive.length > 0 && normal.length > 0 && <Divider />}
        {destructive.map(renderItem)}
      </Menu>
    </>
  );
}
